import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback, memo } from "react"
import { notification } from "components/ui"
import mqtt from "mqtt"
import useStore from "components/ui/Context"
import LivechatRepository from "api/repository/LivechatRepository"
import { maskPhoneNumber } from "../../utils/helper"
import { DfAvatarLogin, IconArrowDown, IconNoChat, IconSend } from "components/ui/Icons"

// Maximum number of messages to keep in DOM
const MAX_MESSAGES = 200
const MESSAGES_TO_REMOVE = 50 // Remove this many oldest messages when limit is reached

// Separate Message component outside to prevent re-renders
const Message = memo(
  ({ item, isNew }) => {
    const [isVisible, setIsVisible] = useState(!isNew)
    const [imgError, setImgError] = useState(false)

    useEffect(() => {
      if (isNew) {
        // Small delay to trigger animation
        const timer = setTimeout(() => setIsVisible(true), 10)
        return () => clearTimeout(timer)
      }
    }, [isNew])

    return (
      <div
        className={`max-w-full py-2 px-4 transition-all duration-300 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="flex items-start gap-3">
          {item?.avatar && !imgError ? (
            <img
              className="h-6 w-6 rounded-full overflow-hidden object-cover"
              src={item?.avatar}
              alt={item?.user_name}
              onError={() => setImgError(true)}
            />
          ) : (
            <img className="h-6 w-6 rounded-full overflow-hidden object-cover" src="/logo/logo.png" alt="avatar" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm leading-relaxed">
              <span className="text-white font-semibold">
                {item?.user_name ? maskPhoneNumber(item.user_name) : `User ${item?.user_id}`}
              </span>
              <span className="text-gray-300 w-full break-words"> {item?.message || ""}</span>
            </p>
          </div>
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if these fields change
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.message === nextProps.item.message &&
      prevProps.item.user_name === nextProps.item.user_name &&
      prevProps.item.avatar === nextProps.item.avatar &&
      prevProps.isNew === nextProps.isNew
    )
  }
)
Message.displayName = "Message"

// Separate MessageList component to isolate from input re-renders
const MessageList = memo(
  ({ isLogin, messages, messageDivRef, newMessageIds, togleModalLogin }) => {
    return (
      <div
        className="relative bg-[#14213C] w-full h-[calc(100%-72px)] md:h-[calc(100%-72px)] overflow-y-scroll scroll-smooth overflow-x-hidden scroll__video"
        ref={messageDivRef}
      >
        {messages?.length > 0 &&
          messages.map((item) => <Message item={item} key={item.id} isNew={!!newMessageIds[item.id]} />)}
        {messages?.length <= 0 && (
          <div className="h-full flex flex-col justify-center items-center text-sm font-normal text-[#AFAFAF]">
            <IconNoChat />
            <p className="text-base text-[#FAFAF9]">Hiện chưa có bình luận</p>
            <p>Hãy là người đầu tiên bình luận!</p>
            {!isLogin && (
              <div
                className="text-[#FAFAF9] font-medium text-sm text-center rounded-xl cursor-pointer bg-[#0097FF] mt-4 px-6 py-1.5"
                onClick={togleModalLogin}
              >
                Đăng nhập để trò chuyện
              </div>
            )}
          </div>
        )}
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if messages array length or loading state changes
    return (
      prevProps.messages.length === nextProps.messages.length &&
      prevProps.loading === nextProps.loading &&
      prevProps.messages[prevProps.messages.length - 1]?.id === nextProps.messages[nextProps.messages.length - 1]?.id
    )
  }
)
MessageList.displayName = "MessageList"

// Separate ChatInput component to isolate text state changes
const ChatInput = memo(({ text, onTextChange, onSubmit, isLogin, isAllowChat, statusChat, errorMessage }) => {
  return (
    <form onSubmit={onSubmit}>
      <div className="absolute left-0 flex bottom-0 w-full justify-center text-[#AFAFAF] gap-2 px-4 p-4 text-xs items-center min-h-[72px] text-center bg-[#28385C] border-t border-[#5F5F5F] ">
        <>
          {!isAllowChat && <>Tính năng bình luận sẽ được mở sau khi phát sóng</>}
          {isAllowChat && (
            <>
              {statusChat === 1 && (
                <>
                  <input
                    value={text}
                    onChange={onTextChange}
                    type="text"
                    className="py-3 px-4 bg-[#14213C] grow placeholder:text-[#FFFFFF99] placeholder:text-xs rounded-full"
                    disabled={!isLogin}
                    placeholder="Trò chuyện với mọi người"
                    maxLength={150}
                  />
                  <div
                    className={`${isLogin ? "cursor-pointer text-primary" : "cursor-not-allowed text-secondary2"}`}
                    onClick={onSubmit}
                  >
                    <IconSend />
                  </div>
                </>
              )}
              {statusChat === 2 && <>{errorMessage}</>}
              {statusChat === 3 && <>Cuộc trò chuyện này đã kết thúc</>}
            </>
          )}
        </>
      </div>
    </form>
  )
})
ChatInput.displayName = "ChatInput"

export default function BoxLiveChat({ dataVideo }) {
  const [isShowChat, setIsShowChat] = useState(true)
  const [text, setText] = useState("")
  const { userInfo, togleModalLogin, setModalBan, logOut } = useStore()
  const [isLogin, setIsLogin] = useState(false)
  const [messages, setMessages] = useState([])
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const [loading, setLoading] = useState(false)
  const [statusChat, setStatusChat] = useState(1)
  const [isAllowChat, setIsAllowChat] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [videoHeight, setVideoHeight] = useState(450)
  const [isMobile, setIsMobile] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  let ws = useRef(null)
  let timeInterval = useRef(null)
  let timeEventTimeout = useRef(null)
  const messageDivRef = useRef(null)
  const lastMessageCountRef = useRef(0)
  const isAtBottomRef = useRef(true)
  const messagesRef = useRef([])
  const newMessageIds = useRef({})
  const newMessageTimeouts = useRef({})
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const videoId = useMemo(() => dataVideo?.id, [dataVideo?.id])

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (isShowChat && messageDivRef.current) {
      messageDivRef.current.scrollTop = messageDivRef.current.scrollHeight
      isAtBottomRef.current = true
    }
  }, [isShowChat])

  useEffect(() => {
    if (!isShowChat) return

    let scrollTimeout

    const handleScroll = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        if (!messageDivRef.current) return
        const { scrollTop, scrollHeight, clientHeight } = messageDivRef.current
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
        setShowScrollBtn(distanceFromBottom > 100) // Tăng threshold
        isAtBottomRef.current = distanceFromBottom <= 100 // Tăng threshold
      }, 50) // Debounce 50ms
    }

    const div = messageDivRef.current

    if (div) {
      div.addEventListener("scroll", handleScroll)
    }

    return () => {
      if (div) div.removeEventListener("scroll", handleScroll)
    }
  }, [isShowChat])

  useEffect(() => {
    if (Object.keys(userInfo).length > 0) setIsLogin(true)
  }, [userInfo])

  useEffect(() => {
    if (!videoId) return
    resetData()
    const diffTime = dataVideo?.start_time - new Date().getTime()
    const diffMinutes = diffTime / 1000 / 60
    if (diffMinutes > 30) {
      setIsAllowChat(false)
      timeEventTimeout.current = setTimeout(() => {
        setIsAllowChat(true)
      }, (diffMinutes - 30) * 60 * 1000)
    } else {
      setIsAllowChat(true)
    }
    return () => {
      if (timeInterval.current) {
        clearInterval(timeInterval.current)
        timeInterval.current = null
      }
      if (timeEventTimeout.current) {
        clearTimeout(timeEventTimeout.current)
        timeEventTimeout.current = null
      }
      if (ws.current) {
        ws.current.unsubscribe(`eventchat/${videoId}`)
        ws.current.end(true)
        ws.current = null
      }

      resetData()
    }
  }, [videoId])

  useEffect(() => {
    if (!videoId || !isAllowChat || !isLogin) return
    checkBanStatus()
    getMessageLiveChat()
    connectWebsocket(videoId)
  }, [videoId, isAllowChat])

  const checkBanStatus = async () => {
    try {
      const res = await LivechatRepository.checkBan()
      if (res?.success) {
        setStatusChat(1)
        setErrorMessage("")
      } else {
        const banMessage = res?.error_message[0]
        setModalBan({
          show: true,
          info: { message: banMessage }
        })
        setStatusChat(2)
        setErrorMessage(banMessage || "Tính năng chat bị hạn chế.")
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        logOut()
        notification.error({
          message: "Phiên đăng nhập hết hạn!"
        })
      } else {
        const errorMessage = error?.response?.data?.error_message[0] || "Đã xảy ra lỗi. Vui lòng thử lại sau!"
        setModalBan({
          show: true,
          info: { message: errorMessage }
        })
        setStatusChat(2)
        setErrorMessage(errorMessage)
      }
    }
  }

  useLayoutEffect(() => {
    if (isAtBottomRef.current && messageDivRef.current) {
      messageDivRef.current.scrollTop = messageDivRef.current.scrollHeight
    }
  }, [messages])

  useEffect(() => {
    const updateVideoHeight = () => {
      const containerElement = document.querySelector("#container")
      if (containerElement) {
        const height = containerElement.offsetHeight
        if (height > 0) setVideoHeight(height)
      }
    }
    updateVideoHeight()
    const observer = new MutationObserver(updateVideoHeight)
    const container = document.querySelector("#container")?.parentElement
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"]
      })
    }
    window.addEventListener("resize", updateVideoHeight)

    return () => {
      observer.disconnect()
    }
  }, [videoId])

  async function connectWebsocket(eventID) {
    try {
      const url = process.env.NEXT_PUBLIC_SOCKET
      let clientId = userInfo.id

      const options = {
        clean: true,
        keepalive: 60,
        protocolVersion: 5,
        reconnectPeriod: 1000,
        connectTimeout: 30000,
        clientId,
        rejectUnauthorized: false,
        properties: {
          topicAliasMaximum: 0,
          sessionExpiryInterval: 0,
          receiveMaximum: 100,
          maximumPacketSize: 10000
        },
        will: {
          topic: "/will",
          payload: "dieout",
          qos: 0,
          retain: false
        }
      }

      ws.current = mqtt.connect(url, options)

      ws.current.on("connect", () => {
        console.log("✅ WebSocket connected")
        const topic = `eventchat/${eventID}`

        ws.current.subscribe(topic, { qos: 0 }, (err) => {
          if (err) console.log("Error subscribing:", err)
        })
      })

      ws.current.on("message", async (topic, payload) => {
        try {
          const data = JSON.parse(payload.toString())

          if (data?.message && data?.user_id && data?.room_id === eventID) {
            setMessages((prevMessages) => {
              // Check if message already exists to prevent duplicates
              const messageExists = prevMessages.some(
                (msg) =>
                  msg.user_id === data.user_id &&
                  msg.message === data.message &&
                  Math.abs(new Date(msg.created_at || Date.now()) - Date.now()) < 1000
              )

              if (messageExists) {
                return prevMessages
              }

              // Create new message object
              const newMessage = {
                id: `${data.user_id}-${Date.now()}`,
                user_id: data.user_id,
                user_name: data.user_name,
                avatar: data.avatar,
                message: data.message,
                raw_message: data.raw_message,
                room_id: data.room_id,
                created_at: new Date().toISOString()
              }

              const updatedMessages = [...prevMessages, newMessage]

              // Mark this message as new for animation
              newMessageIds.current[newMessage.id] = true

              // Remove the "new" flag after animation completes
              const timeoutId = setTimeout(() => {
                delete newMessageIds.current[newMessage.id]
                delete newMessageTimeouts.current[newMessage.id]
              }, 500) // Match animation duration

              newMessageTimeouts.current[newMessage.id] = timeoutId

              // Remove old messages if exceeding MAX_MESSAGES
              if (updatedMessages.length > MAX_MESSAGES) {
                console.log(`Removing ${MESSAGES_TO_REMOVE} old messages to optimize performance`)
                const removedMessages = updatedMessages.slice(0, MESSAGES_TO_REMOVE)

                // Clean up timeouts and new message flags for removed messages
                removedMessages.forEach((msg) => {
                  const timeoutId = newMessageTimeouts.current[msg.id]
                  if (timeoutId) {
                    clearTimeout(timeoutId)
                    delete newMessageTimeouts.current[msg.id]
                  }
                  delete newMessageIds.current[msg.id]
                })

                return updatedMessages.slice(MESSAGES_TO_REMOVE)
              }

              return updatedMessages
            })
          }
        } catch (error) {
          console.error("Error processing message:", error)
        }
      })

      ws.current.on("close", () => {
        console.log("WebSocket closed")
      })

      ws.current.on("error", (err) => {
        console.error("❌ WebSocket error", err)
      })

      ws.current.on("disconnect", () => {
        console.log("Disconnected from MQTT")
      })

      ws.current.on("offline", () => {
        console.log("Client went offline")
      })
    } catch (error) {
      console.error("Error establishing WebSocket connection:", error)
    }
  }

  async function getMessageLiveChat() {
    try {
      setLoading(true)

      const res = await LivechatRepository.getMessageLiveChat({
        room_id: videoId
      })

      setLoading(false)

      let newMessages = res.data || []

      // If initial messages exceed MAX_MESSAGES, only keep the most recent ones
      if (newMessages.length > MAX_MESSAGES) {
        console.log(`Initial messages (${newMessages.length}) exceed limit, keeping last ${MAX_MESSAGES}`)
        newMessages = newMessages.slice(-MAX_MESSAGES)
      }

      setMessages(newMessages)
    } catch (error) {
      console.log("Error fetching messages:", error)
      setLoading(false)
    }
  }

  // const handleCloseChat = () => {
  //   setIsShowChat(false)
  //   setShowScrollBtn(false)
  //   if (messageDivRef.current) {
  //     messageDivRef.current.scrollTo({
  //       top: messageDivRef.current.scrollHeight,
  //       behavior: "smooth"
  //     })
  //   }
  // }

  const handleSendComment = async (e) => {
    e?.preventDefault()
    if (!isLogin) {
      notification.warning({ message: "Vui lòng đăng nhập để bình luận" })
      return
    }
    const trimmedText = text.trim()
    if (!trimmedText) {
      notification.warning({ message: "Vui lòng nhập bình luận" })
      return
    }
    if (isSubmitting) return

    try {
      setIsSubmitting(true)
      setText("")

      await checkBanStatus()

      if (statusChat === 1) {
        const res = await LivechatRepository.sendMessageLiveChat({
          room_id: videoId,
          message: trimmedText
        })

        if (!res?.success) {
          throw new Error(res?.message || "Gửi bình luận thất bại")
        }
      }
    } catch (error) {
      console.log("Error sending comment:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const scrollToBottom = useCallback(() => {
    if (messageDivRef.current) {
      messageDivRef.current.scrollTo({
        top: messageDivRef.current.scrollHeight,
        behavior: "smooth"
      })
    }
  }, [])

  const resetData = () => {
    setMessages([])
    setStatusChat(1)
    setIsAllowChat(false)
    lastMessageCountRef.current = 0

    // Clear all animation timeouts
    Object.values(newMessageTimeouts.current).forEach((timeoutId) => clearTimeout(timeoutId))
    newMessageTimeouts.current = {}
    newMessageIds.current = {}
  }

  // Memoize the input change handler to prevent recreation on every render
  const handleTextChange = useCallback((e) => {
    setText(e.target.value)
  }, [])

  const renderUIChat = () => {
    if (dataVideo?.is_live && !isMobile) {
      return (
        <>
          <div className="relative overflow-hidden" style={{ height: `61vh` }}>
            {/* <div className="flex items-center justify-between font-semibold pt-5 pb-3 px-4 border-b border-[#5F5F5F]">
                <div className="text-secondary">Trò chuyện</div>
                <button
                  className="w-8 h-8 flex justify-center items-center rounded-full backdrop-blur-xl border border-white/[0.04]"
                  onClick={handleCloseChat}
                >
                  <IconArrowUp />
                </button>
              </div> */}

            <MessageList
              isLogin={isLogin}
              messages={messages}
              loading={loading}
              messageDivRef={messageDivRef}
              newMessageIds={newMessageIds.current}
              togleModalLogin={togleModalLogin}
            />

            <div
              onClick={scrollToBottom}
              className={`absolute w-full text-center bottom-[82px] flex justify-center transition-opacity duration-500 ${
                showScrollBtn ? "z-10 opacity-100" : "-z-10 opacity-0"
              }`}
            >
              <div className="flex items-center justify-center rounded-full whitespace-nowrap bg-[#0097FF] text-xs text-white px-3 py-1 gap-1 cursor-pointer">
                Tin nhắn mới
                <IconArrowDown />
              </div>
            </div>

            {isLogin && (
              <ChatInput
                text={text}
                onTextChange={handleTextChange}
                onSubmit={handleSendComment}
                isLogin={isLogin}
                isAllowChat={isAllowChat}
                statusChat={statusChat}
                errorMessage={errorMessage}
              />
            )}
          </div>
        </>
      )
    }
  }

  return <div>{renderUIChat()}</div>
}
